import type { ReactNode } from "react";

import { useAuth } from "@/shared/contexts/AuthProvider";
import type { PortalPermission } from "@/features/auth/access-config";

/**
 * Route guard: renders `children` only when the user holds `permission`.
 * Fail-closed (delegates to `can()`, which is false while loading / on error).
 *
 * On denial it renders an inline access panel rather than redirecting: there is
 * no permission-free Home route (`/` → `/planner`, itself gated), so a redirect
 * would loop. The nav already hides links the user can't open; this covers
 * direct URL access.
 */
export function RequirePermission({
  permission,
  children,
}: {
  permission: PortalPermission;
  children: ReactNode;
}) {
  const { can } = useAuth();

  if (!can(permission)) {
    return (
      <div
        className="flex min-h-[60vh] items-center justify-center px-6"
        data-testid="access-denied"
      >
        <div className="w-full max-w-md rounded-2xl border border-border-primary bg-background-card p-8 text-center shadow-sm">
          <h1 className="text-lg font-semibold text-text-primary">
            You do not have access to this page
          </h1>
          <p className="mt-2 text-sm text-text-secondary">
            Ask an administrator if you need access.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
